import Model, { attr, belongsTo, hasMany } from '@warp-drive/legacy/model';
import { service } from '@ember/service';
import type Intl from 'ember-intl/services/intl';
import type { Store } from '@warp-drive/core';
import type PslfProfileModel from './pslf-profile.ts';
import type AttachmentModel from './attachment.ts';
import type PslfFormVersionModel from './pslf-form-version.ts';
import type PslfGroupModel from './pslf-group.ts';
import type PslfTemplateModel from './pslf-template.ts';
import { Type } from '@warp-drive/core-types/symbols';
import type EmployeeModel from './employee.ts';

export type BorrowerFields = {
  'borrower-address': string;
  'borrower-address-verification-status'?: 'valid' | 'invalid' | 'unverified';
  'borrower-alternate-phone': string;
  'borrower-apartment': string;
  'borrower-birth-date': string;
  'borrower-city': string;
  'borrower-email': string;
  'borrower-employer-contact-name': string;
  'borrower-employer-contact-phone-number': string;
  'borrower-employer-contact-title': string;
  'borrower-employer-ein': string;
  'borrower-employer-email': string;
  'borrower-employer-name': string;
  'borrower-employer-website': string;
  'borrower-employment-continues': boolean;
  'borrower-employment-end-date': string;
  'borrower-employment-hours': string;
  'borrower-employment-start-date': string;
  'borrower-employment-status': string;
  'borrower-name': string;
  'borrower-non-partner-form': boolean;
  'borrower-primary-phone': string;
  'borrower-print-and-mail': boolean;
  'borrower-prior-employer-closed': boolean;
  'borrower-reason': string;
  'borrower-sign-date': string;
  'borrower-state': string;
  'borrower-wants-forbearance': boolean;
  'borrower-zip': string;
  'embed-url': string;
};

// ---------------------------------------------------------------------------
// Status groups (shared by dashboards & alerts)
// ---------------------------------------------------------------------------

/** Statuses representing a form that has been submitted to the government. */
export const SUBMITTED_STATUSES = ['submitted', 're_submitted'] as const;

/** Statuses representing a submit attempt that's in-flight (being faxed). */
export const PENDING_SUBMIT_STATUSES = ['submit_pending', 're_submit_pending'] as const;

/** Statuses where the form has been signed but not yet submitted. */
export const PENDING_SIGNATURE_STATUSES = [
  'borrower_signed',
  'employer_signed',
  'awaiting_submit',
] as const;

/** Statuses considered "active" (worth surfacing in alerts / dashboards). */
export const ACTIVE_STATUSES = [
  ...SUBMITTED_STATUSES,
  ...PENDING_SUBMIT_STATUSES,
  ...PENDING_SIGNATURE_STATUSES,
] as const;

/** Statuses representing a terminal/dead form. */
export const INACTIVE_STATUSES = ['rejected', 'cancelled', 'expired', 'abandoned'] as const;

/** ~12 months: a submitted form is still considered "recent". */
export const ONE_YEAR_DAYS = 365;

/** ~11 months: nudge the user to recertify / show an "expiring" hint. */
export const RECERT_WARNING_DAYS = 335;

const MS_PER_DAY = 1000 * 60 * 60 * 24;

export type EmployerFields = {
  'employer-apartment': string;
  'employer-city': string;
  'employer-contact-email': string;
  'employer-contact-name': string;
  'employer-contact-phone-number': string;
  'employer-contact-title': string;
  'employer-email': string;
  'employer-fein': string;
  'employer-group-id': string;
  'employer-name': string;
  'employer-sign-date': string;
  'employer-state': string;
  'employer-street-address': string;
  'employer-website': string;
  'employer-zip': string;
  'employment-continues': boolean;
  'employment-end-date': string;
  'employment-hours': string;
  'employment-start-date': string;
  'employment-status': string;
};

export default class PslfFormModel extends Model {
  @service declare intl: Intl;
  @service declare store: Store;

  @attr declare status: string;
  @attr declare documentId: string;
  @attr declare payrollId: string;
  @attr declare faxId: string;
  @attr declare borrowerFields: BorrowerFields;
  @attr declare employerFields: EmployerFields;
  @attr declare borrowerCompletedAt: string;
  @attr declare employerCompletedAt: string;
  @attr declare submittedAt: string;
  @attr declare formType: string;
  @attr declare isNonPartner: boolean;
  @attr declare isPreApproved: boolean;
  @attr declare employerSignLink: string;
  @attr declare faxVerified: number;
  @attr declare submittedOnBehalfOfById: string | null;

  @attr declare createdAt: string;
  @attr declare updatedAt: string;

  // this is so calls from the v1 controller can tell us we are connected to a template
  // it can be removed once all calls are using the v2 controllers
  @attr declare pslfTemplateId: string;

  /*************************
   **  Relationships      **
   *************************/

  @belongsTo('pslf-profile', { async: false, inverse: 'pslfForms' })
  declare pslfProfile: PslfProfileModel;
  @belongsTo('employee', { async: false, inverse: null })
  declare employee: EmployeeModel;
  @belongsTo('pslf-profile', { async: false, inverse: null })
  declare approverPslfProfile: PslfProfileModel;
  @hasMany('attachment', { async: false, inverse: null })
  declare attachments: AttachmentModel[];
  @hasMany('pslf-form-version', { async: false, inverse: null })
  declare versions: PslfFormVersionModel[];
  @belongsTo('pslf-group', { async: false, inverse: 'pslfForms' })
  declare pslfGroup: PslfGroupModel;
  @belongsTo('pslf-template', { async: false, inverse: 'pslfForms' })
  declare pslfTemplate: PslfTemplateModel;

  /**************************
   **  Computed Properties **
   **************************/

  get isPriorEmployer() {
    return this.formType === 'priorEmployer';
  }

  get isCurrentEmployer() {
    return this.formType === 'currentEmployer';
  }

  get isNonPartnerApproverForm() {
    return this.isNonPartner;
  }

  get isPriorPeriodForm() {
    return this.isPriorEmployer && !this.isNonPartnerApproverForm;
  }

  /**
   * Returns a translation-key suffix that distinguishes between
   * prior-period forms at the current employer vs truly-different
   * former employer forms.
   */
  get formTypeDisplayKey(): string {
    if (this.isPriorPeriodForm) return 'currentEmployerPriorPeriod';
    return this.formType;
  }

  get isOboForm() {
    return !!this.submittedOnBehalfOfById;
  }

  get isPriorEmployerClosed() {
    return !!this.borrowerFields?.['borrower-prior-employer-closed'];
  }

  get isPrintAndMail() {
    return !!this.borrowerFields?.['borrower-print-and-mail'];
  }

  get employerDisplayName() {
    return (
      this.borrowerFields?.['borrower-employer-name'] ||
      this.employerFields?.['employer-name'] ||
      ''
    );
  }

  get fullEmployerAddress() {
    const line1 = this.employerFields?.['employer-street-address'] || '';
    const line2 = this.employerFields?.['employer-apartment'] || '';
    const city = this.employerFields?.['employer-city'] || '';
    const state = this.employerFields?.['employer-state'] || '';
    const zip = this.employerFields?.['employer-zip'] || '';

    // Build street portion (line1 and line2)
    const streetParts = [line1, line2].filter(Boolean);
    const streetAddress = streetParts.join(' ');

    // Build city/state/zip portion
    const cityStateZipParts = [];
    if (city) {
      cityStateZipParts.push(city);
    }
    // Combine state and zip
    const stateZip = [state, zip].filter(Boolean).join(' ');
    if (stateZip) {
      cityStateZipParts.push(stateZip);
    }
    const cityStateZip = cityStateZipParts.join(', ');

    // Combine all parts
    const allParts = [streetAddress, cityStateZip].filter(Boolean);
    return allParts.join(', ');
  }

  // TODO: Type employer fields so inference will work.
  get approverDisplayEmail() {
    if (this.isPrintAndMail) {
      return `${this.intl.t('pslf.table.not_applicable')} - ${this.intl.t(
        'pslf.table.print_and_mail'
      )}`;
    }
    if (this.isPriorEmployerClosed) {
      return `${this.intl.t('pslf.table.not_applicable')} - ${this.intl.t(
        'pslf.table.prior_employer_closed'
      )}`;
    }
    return (
      this.employerFields?.['employer-email'] ||
      this.employerFields?.['employer-contact-email'] ||
      ''
    );
  }

  get isAwaitingSignature() {
    return ['created', 'borrower_signed'].includes(this.status);
  }

  get needsReview() {
    return this.status === 'borrower_signed' && !this.isNonPartner;
  }

  get isEmployerSignLinkValid() {
    if (this.isPriorEmployerClosed || this.isPrintAndMail) {
      return false;
    }
    return this.isAwaitingSignature;
  }

  get hasPdfPreview() {
    return this.attachments.length > 0;
  }

  get canTransferPdf() {
    return !!this.pslfTemplateId && this.status !== 'created';
  }

  get canFax() {
    return [
      'awaiting_submit',
      'submit_errored',
      'submitted',
      're_submitted',
      're_submit_errored',
    ].includes(this.status);
  }

  get canCancel() {
    return ['borrower_signed', 'employer_signed', 'awaiting_submit', 'submit_errored'].includes(
      this.status
    );
  }

  get canChangeApprover() {
    if (this.isPriorEmployerClosed || this.isPrintAndMail) {
      return false;
    }

    return ['borrower_signed'].includes(this.status) && this.isNonPartnerApproverForm;
  }

  get canResendEmail() {
    return (
      ['borrower_signed'].includes(this.status) &&
      !this.isPrintAndMail &&
      !this.isPriorEmployerClosed
    );
  }

  // ------------------------------------------------------------------
  // Dashboard status predicates (v2 PSLF dashboard)
  // ------------------------------------------------------------------

  get isSubmitted() {
    return (SUBMITTED_STATUSES as readonly string[]).includes(this.status);
  }

  get isPendingSubmit() {
    return (PENDING_SUBMIT_STATUSES as readonly string[]).includes(this.status);
  }

  get isSubmittedOrPending() {
    return this.isSubmitted || this.isPendingSubmit;
  }

  get isPendingSignature() {
    return (PENDING_SIGNATURE_STATUSES as readonly string[]).includes(this.status);
  }

  get isActiveStatus() {
    return (ACTIVE_STATUSES as readonly string[]).includes(this.status);
  }

  get isInactiveStatus() {
    return (INACTIVE_STATUSES as readonly string[]).includes(this.status);
  }

  // ------------------------------------------------------------------
  // Time helpers
  // ------------------------------------------------------------------

  /**
   * Days since the form was submitted. Falls back to createdAt when no
   * submittedAt is present. Returns 0 when neither is available.
   */
  get daysSinceSubmitted() {
    const referenceDate = this.submittedAt || this.createdAt;
    if (!referenceDate) return 0;
    return (Date.now() - new Date(referenceDate).getTime()) / MS_PER_DAY;
  }

  /** True when the form was submitted within the last ~12 months. */
  get isRecentlySubmitted() {
    if (!this.isSubmitted) return false;
    return this.daysSinceSubmitted < ONE_YEAR_DAYS;
  }

  /** True when a submitted form is 11+ months old and should prompt recert. */
  get isExpiring() {
    if (!this.isSubmittedOrPending) return false;
    return this.daysSinceSubmitted >= RECERT_WARNING_DAYS;
  }

  // ------------------------------------------------------------------
  // Field extraction (JSONB key coupling lives here and only here)
  // ------------------------------------------------------------------

  get employmentStartDate() {
    return (
      this.borrowerFields?.['borrower-employment-start-date'] ||
      this.employerFields?.['employment-start-date'] ||
      null
    );
  }

  get employmentEndDate() {
    return (
      this.borrowerFields?.['borrower-employment-end-date'] ||
      this.employerFields?.['employment-end-date'] ||
      null
    );
  }

  /**
   * Pick the most meaningful timestamp on a form for user-facing display,
   * along with the intl key that describes it.
   */
  get mostRelevantDate() {
    if (this.submittedAt) {
      return { date: this.submittedAt, key: 'pslf.reimagined.form_status.submitted_on' };
    }
    if (this.employerCompletedAt) {
      return { date: this.employerCompletedAt, key: 'pslf.reimagined.form_status.signed_on' };
    }
    if (this.borrowerCompletedAt) {
      return { date: this.borrowerCompletedAt, key: 'pslf.reimagined.form_status.signed_on' };
    }
    if (this.createdAt) {
      return { date: this.createdAt, key: 'pslf.reimagined.form_status.created_on' };
    }
    return null;
  }

  /**************************
   **  Methods **
   **************************/

  async sendFax() {
    return this.store.adapterFor('pslf-form').sendFax(this);
  }

  async transferPdf() {
    return this.store.adapterFor('pslf-form').transferPdf(this);
  }

  [Type] = 'pslf-form' as const;
}
